import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "@/axiosSetup";
import { AppDispatch } from "@/redux/store";
import { IMeetingMinutes } from "../../../types/IMeetingMinutes";
import { FileOrFolder } from "../../../types/FileOrFolder";
import { AxiosError } from "axios";

// interface MeetingMinutesState {
//   meetingMinuteFile: FileOrFolder | null;
//   supportingDocs: FileOrFolder[];
//   initLoading: boolean;
//   error: string | null;
// }
interface MeetingMinutesState {
  meetingMinuteFile: FileOrFolder | null;
  meetingMinuteFiles: FileOrFolder[];
  supportingDocs: FileOrFolder[];
  initLoading: boolean;
  error: string | null;
}

// const initialState: MeetingMinutesState = {
//   meetingMinuteFile: null,
//   supportingDocs: [],
//   // meetingMinutes: [],
//   // currentMeetingMinutes: null,
//   initLoading: false,
//   error: null,
// };
const initialState: MeetingMinutesState = {
  meetingMinuteFile: null,
  meetingMinuteFiles: [],
  supportingDocs: [],
  initLoading: false,
  error: null,
};

const meetingMinutesSlice = createSlice({
  name: "meetingMinutes",
  initialState,
  reducers: {
    setMeetingMinuteFileIfCurrent: (
      state,
      action: PayloadAction<{
        file: FileOrFolder | null;
        mode?: string;
      }>
    ) => {
      const { file } = action.payload;

      if (!file) return;
      if (!state.meetingMinuteFile) return;

      const currentFileId = String(state.meetingMinuteFile._id || "");
      const nextFileId = String(file._id || "");

      const currentMeetingMinuteId = String(
        state.meetingMinuteFile.meetingMinuteContent?._id || ""
      );

      const nextMeetingMinuteId = String(
        file.meetingMinuteContent?._id || ""
      );

      const isSameMeetingMinute =
        currentFileId === nextFileId ||
        (!!currentMeetingMinuteId &&
          currentMeetingMinuteId === nextMeetingMinuteId);

      if (!isSameMeetingMinute) return;

      state.meetingMinuteFile = file;
    },
    setMeetingMinuteFile: (
        state,
        action: PayloadAction<{
          file: FileOrFolder | null;
          mode?: string; // OPTIONAL!
        }>
        ) => {
      const { file, mode } = action.payload;
      if (!file) {
        state.meetingMinuteFile = null;
        return;
      }
      state.meetingMinuteFile = file;
    },
    setMeetingMinutesLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload;
    },
    setMeetingMinutesError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearMeetingMinutes: (state) => {
      // state.meetingMinutes = [];
      // state.currentMeetingMinutes = null;
      state.initLoading = false;
      state.error = null;
    },
    renameSupportingFileMM: (state, action: PayloadAction<{ docId: string, newName: string }>) => {
        const { docId, newName } = action.payload;
        const mmFile = state.meetingMinuteFile;
        const meetingMinute = mmFile?.meetingMinuteContent;

        if (!mmFile || !meetingMinute) return;

        // Ensure supportingDocs array exists
        if (!meetingMinute.supportingDocs) {
            meetingMinute.supportingDocs = [];
        }

        meetingMinute.supportingDocs = meetingMinute.supportingDocs.map(doc => ({
            ...doc,
            name: doc._id === docId ? newName : doc.name,
        }));
      
    },
    // NEW
    setMeetingMinuteFiles: (
      state,
      action: PayloadAction<FileOrFolder[]>
    ) => {
      state.meetingMinuteFiles = action.payload;
    },

    upsertMeetingMinuteFile: (
      state,
      action: PayloadAction<FileOrFolder>
    ) => {
      const incoming = action.payload;
      if (!incoming?._id) return;

      const index = state.meetingMinuteFiles.findIndex(
        (file) => String(file._id) === String(incoming._id)
      );

      if (index !== -1) {
        state.meetingMinuteFiles[index] = incoming;
      } else {
        state.meetingMinuteFiles.unshift(incoming);
      }

      if (
        state.meetingMinuteFile?._id &&
        String(state.meetingMinuteFile._id) === String(incoming._id)
      ) {
        state.meetingMinuteFile = incoming;
      }
    },

    removeMeetingMinuteFileFromList: (
      state,
      action: PayloadAction<string>
    ) => {
      state.meetingMinuteFiles = state.meetingMinuteFiles.filter(
        (file) => String(file._id) !== String(action.payload)
      );

      if (
        state.meetingMinuteFile?._id &&
        String(state.meetingMinuteFile._id) === String(action.payload)
      ) {
        state.meetingMinuteFile = null;
      }
    },
  },
  extraReducers: (builder) => {
      builder
      //Fetch BCR File
      .addCase(fetchMeetingMinuteFile.pending, (state) => {
            state.initLoading = true;
            state.error = null;
        })
        .addCase(fetchMeetingMinuteFile.fulfilled, (state, action: PayloadAction<FileOrFolder>) => {
            state.initLoading = false;
            state.meetingMinuteFile = action.payload;
            // console.log('meetingMinuteFile', action.payload);
        })
        .addCase(fetchMeetingMinuteFile.rejected, (state, action) => {
            state.initLoading = false;
            state.error = action.payload || 'Failed to fetch meeting minute file';
        })
        // NEW
        .addCase(fetchMeetingMinuteFiles.pending, (state) => {
          state.initLoading = true;
          state.error = null;
        })
        .addCase(
          fetchMeetingMinuteFiles.fulfilled,
          (state, action: PayloadAction<FileOrFolder[]>) => {
            state.initLoading = false;
            state.meetingMinuteFiles = action.payload;
          }
        )
        .addCase(fetchMeetingMinuteFiles.rejected, (state, action) => {
          state.initLoading = false;
          state.error = action.payload || "Failed to fetch meeting minute files";
        })
      }
});

export const fetchMeetingMinuteFile = createAsyncThunk<
  FileOrFolder,              // Return type
  string,                    // Argument type (the BCR file ID)
  { rejectValue: string }    // Optional error type
>(
  'meetingMinutes/fetchMeetingMinuteFile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/meeting-minutes/file/${id}`);
      return response.data.file;
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch meeting minute file');
    }
  }
);

export const fetchMeetingMinuteFiles = createAsyncThunk<
  FileOrFolder[],
  void,
  { rejectValue: string }
>(
  "meetingMinutes/fetchMeetingMinuteFiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/api/files/meeting-minute-files", {
        withCredentials: true,
      });

      return response.data.files || [];
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch meeting minute files"
      );
    }
  }
);

// type CreateMeetingMinutesPayload = {
//   name: string;
//   parentId?: string | null;
//   type?: string;
//   content?: string | null;
//   url?: string | null;
//   s3Key?: string | null;
//   x?: number | null;
//   y?: number | null;
//   accessLevel?: string;
// };

export type SearchMeetingMinutesPayload = {
  keywords: string[];
  keywordMode?: "any" | "all";
  selectedMetaData: string[];
  docTypes: string[];
  startDateCreation: string | null;
  endDateCreation: string | null;
  startDateMeeting: string | null;
  endDateMeeting: string | null;
};

// export const searchMeetingMinutesAdvanced =
//   (payload: SearchMeetingMinutesPayload) => async (dispatch: AppDispatch) => {
//     try {
//       dispatch(setMeetingMinutesLoading(true));
//       dispatch(setMeetingMinutesError(null));

//       const { data } = await api.post("/api/meeting-minutes/search", payload);

//       dispatch(setMeetingMinutes(data.meetingMinutes || []));
//       return data.meetingMinutes;
//     } catch (error: any) {
//       dispatch(
//         setMeetingMinutesError(
//           error?.response?.data?.message || "Failed to search meeting minutes"
//         )
//       );
//       throw error;
//     } finally {
//       dispatch(setMeetingMinutesLoading(false));
//     }
// };

export const {
  setMeetingMinuteFileIfCurrent,
  setMeetingMinuteFile,
  setMeetingMinuteFiles,
  upsertMeetingMinuteFile,
  removeMeetingMinuteFileFromList,
  renameSupportingFileMM,
  setMeetingMinutesLoading,
  setMeetingMinutesError,
  clearMeetingMinutes,
} = meetingMinutesSlice.actions;

export default meetingMinutesSlice.reducer;